import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Weight, Heart, Plus, TrendingDown, Calendar } from 'lucide-react';
import { mockHealthLogs, mockJournalEntries } from '../mock';
import { toast } from '../hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const HealthTracking = () => {
  const [healthLogs, setHealthLogs] = useState(mockHealthLogs);
  const [journalEntries, setJournalEntries] = useState(mockJournalEntries);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const [newHealthLog, setNewHealthLog] = useState({
    weight: '',
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    notes: '',
    date: new Date().toISOString().slice(0, 10)
  });

  const [newJournal, setNewJournal] = useState({
    mood: 'Good',
    energy_level: 5,
    stress_level: 5,
    notes: '',
    symptoms: ''
  });

  const handleAddHealthLog = () => {
    if (!newHealthLog.weight) {
      toast({ title: "Error", description: "Please enter your weight", variant: "destructive" });
      return;
    }

    const log = {
      log_id: `health_${Date.now()}`,
      user_id: 'user_001',
      weight: parseFloat(newHealthLog.weight),
      blood_pressure_systolic: parseInt(newHealthLog.blood_pressure_systolic) || null,
      blood_pressure_diastolic: parseInt(newHealthLog.blood_pressure_diastolic) || null,
      date: new Date(newHealthLog.date),
      notes: newHealthLog.notes
    };

    setHealthLogs([log, ...healthLogs]);
    setDialogOpen(false);
    setNewHealthLog({
      weight: '',
      blood_pressure_systolic: '',
      blood_pressure_diastolic: '',
      notes: '',
      date: new Date().toISOString().slice(0, 10)
    });

    toast({ title: "Success!", description: "Health data logged successfully" });
  };

  const handleAddJournal = () => {
    const entry = {
      entry_id: `journal_${Date.now()}`,
      user_id: 'user_001',
      date: new Date(),
      mood: newJournal.mood,
      energy_level: parseInt(newJournal.energy_level),
      stress_level: parseInt(newJournal.stress_level),
      notes: newJournal.notes,
      symptoms: newJournal.symptoms ? newJournal.symptoms.split(',').map(s => s.trim()) : []
    };

    setJournalEntries([entry, ...journalEntries]);
    setJournalDialogOpen(false);
    setNewJournal({
      mood: 'Good',
      energy_level: 5,
      stress_level: 5,
      notes: '',
      symptoms: ''
    });

    toast({ title: "Success!", description: "Journal entry added" });
  };

  // Calculate BMI from latest weight (assuming height 1.75m)
  const latestWeight = healthLogs[0]?.weight || 0;
  const height = 1.75; // meters
  const bmi = latestWeight > 0 ? (latestWeight / (height * height)).toFixed(1) : 0;

  const avgBP = healthLogs.length > 0
    ? `${Math.round(healthLogs.reduce((sum, log) => sum + (log.blood_pressure_systolic || 0), 0) / healthLogs.length)}/${Math.round(healthLogs.reduce((sum, log) => sum + (log.blood_pressure_diastolic || 0), 0) / healthLogs.length)}`
    : 'N/A';

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white shadow-lg">
        <h2 className="text-3xl font-bold mb-2">Health Tracking</h2>
        <p className="text-purple-100 text-lg">Monitor weight, blood pressure, and daily wellness</p>
      </div>

      <Tabs defaultValue="vitals" className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="vitals">Vitals</TabsTrigger>
          <TabsTrigger value="journal">Daily Journal</TabsTrigger>
        </TabsList>

        {/* Vitals Tab */}
        <TabsContent value="vitals" className="space-y-6">
          <div className="flex justify-end">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Log Vitals
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Log Health Data</DialogTitle>
                  <DialogDescription>Record your weight and blood pressure</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight">Weight (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="0.1"
                      placeholder="75.5"
                      value={newHealthLog.weight}
                      onChange={(e) => setNewHealthLog({ ...newHealthLog, weight: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="bp-sys">BP Systolic</Label>
                      <Input
                        id="bp-sys"
                        type="number"
                        placeholder="120"
                        value={newHealthLog.blood_pressure_systolic}
                        onChange={(e) => setNewHealthLog({ ...newHealthLog, blood_pressure_systolic: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bp-dia">BP Diastolic</Label>
                      <Input
                        id="bp-dia"
                        type="number"
                        placeholder="80"
                        value={newHealthLog.blood_pressure_diastolic}
                        onChange={(e) => setNewHealthLog({ ...newHealthLog, blood_pressure_diastolic: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newHealthLog.date}
                      onChange={(e) => setNewHealthLog({ ...newHealthLog, date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any observations..."
                      value={newHealthLog.notes}
                      onChange={(e) => setNewHealthLog({ ...newHealthLog, notes: e.target.value })}
                    />
                  </div>
                  <Button onClick={handleAddHealthLog} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
                    Log Data
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="shadow-md border-0">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Current Weight</CardTitle>
                <Weight className="w-5 h-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{latestWeight} kg</div>
                <p className="text-sm text-gray-600 mt-1">BMI: {bmi}</p>
              </CardContent>
            </Card>

            <Card className="shadow-md border-0">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Blood Pressure</CardTitle>
                <Heart className="w-5 h-5 text-red-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{avgBP}</div>
                <p className="text-sm text-gray-600 mt-1">Average mmHg</p>
              </CardContent>
            </Card>

            <Card className="shadow-md border-0">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Weight Change</CardTitle>
                <TrendingDown className="w-5 h-5 text-emerald-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-emerald-600">-0.7 kg</div>
                <p className="text-sm text-gray-600 mt-1">Last 7 days</p>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-md border-0">
            <CardHeader>
              <CardTitle>Health History</CardTitle>
              <CardDescription>Your recent vital measurements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {healthLogs.map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {new Date(log.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </p>
                      {log.notes && <p className="text-sm text-gray-600 mt-1">{log.notes}</p>}
                    </div>
                    <div className="flex gap-6 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-purple-700">{log.weight} kg</p>
                        <p className="text-xs text-gray-600">Weight</p>
                      </div>
                      {log.blood_pressure_systolic && (
                        <div className="text-center">
                          <p className="font-bold text-red-700">
                            {log.blood_pressure_systolic}/{log.blood_pressure_diastolic}
                          </p>
                          <p className="text-xs text-gray-600">BP</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Journal Tab */}
        <TabsContent value="journal" className="space-y-6">
          <div className="flex justify-end">
            <Dialog open={journalDialogOpen} onOpenChange={setJournalDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600">
                  <Plus className="w-4 h-4 mr-2" />
                  New Entry
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Daily Journal Entry</DialogTitle>
                  <DialogDescription>How are you feeling today?</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="mood">Mood</Label>
                    <Select value={newJournal.mood} onValueChange={(value) => setNewJournal({ ...newJournal, mood: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Excellent">Excellent</SelectItem>
                        <SelectItem value="Great">Great</SelectItem>
                        <SelectItem value="Good">Good</SelectItem>
                        <SelectItem value="Fair">Fair</SelectItem>
                        <SelectItem value="Poor">Poor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="energy">Energy Level (1-10)</Label>
                    <Input
                      id="energy"
                      type="number"
                      min="1"
                      max="10"
                      value={newJournal.energy_level}
                      onChange={(e) => setNewJournal({ ...newJournal, energy_level: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="stress">Stress Level (1-10)</Label>
                    <Input
                      id="stress"
                      type="number"
                      min="1"
                      max="10"
                      value={newJournal.stress_level}
                      onChange={(e) => setNewJournal({ ...newJournal, stress_level: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="symptoms">Symptoms (comma-separated)</Label>
                    <Input
                      id="symptoms"
                      placeholder="e.g., Fatigue, Thirst"
                      value={newJournal.symptoms}
                      onChange={(e) => setNewJournal({ ...newJournal, symptoms: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="journal-notes">Notes</Label>
                    <Textarea
                      id="journal-notes"
                      placeholder="How was your day?"
                      value={newJournal.notes}
                      onChange={(e) => setNewJournal({ ...newJournal, notes: e.target.value })}
                    />
                  </div>
                  <Button onClick={handleAddJournal} className="w-full bg-gradient-to-r from-blue-500 to-cyan-500">
                    Save Entry
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {journalEntries.map((entry, index) => {
              const moodColors = {
                Excellent: 'bg-green-100 text-green-700',
                Great: 'bg-emerald-100 text-emerald-700',
                Good: 'bg-blue-100 text-blue-700',
                Fair: 'bg-yellow-100 text-yellow-700',
                Poor: 'bg-red-100 text-red-700'
              };

              return (
                <Card key={index} className="shadow-md border-0">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <CardTitle className="text-lg">
                          {new Date(entry.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </CardTitle>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${moodColors[entry.mood]}`}>
                        {entry.mood}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-6 text-sm">
                      <div>
                        <p className="text-gray-600">Energy</p>
                        <p className="font-bold text-blue-700">{entry.energy_level}/10</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Stress</p>
                        <p className="font-bold text-orange-700">{entry.stress_level}/10</p>
                      </div>
                    </div>
                    {entry.notes && (
                      <p className="text-gray-700 leading-relaxed">{entry.notes}</p>
                    )}
                    {entry.symptoms.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Symptoms:</p>
                        <div className="flex flex-wrap gap-2">
                          {entry.symptoms.map((symptom, i) => (
                            <span key={i} className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                              {symptom}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HealthTracking;
